import { useMemo } from 'react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useInvoiceItems, type InvoiceItemRow } from '@/api/invoices';
import { formatDate, formatMoney } from '@/lib/format';
import { EntityLink } from '@/components/entity-link';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** Read a numeric field, tolerating the legacy alias when the primary is absent. */
function num(row: InvoiceItemRow, primary: keyof InvoiceItemRow, legacy: string): number | undefined {
  const v = (row[primary] ?? row[legacy]) as unknown;
  return typeof v === 'number' ? v : undefined;
}

const right = { textAlign: 'right' as const };

/**
 * Items tab — the invoice's line items in a DataTable: name, quantity, unit
 * price, tax %, amount (money right-aligned). Many invoices (e.g. merge
 * invoices) return no items, so an EmptyState is the expected state.
 */
export function InvoiceItemsTab({ id, currency }: { id: number; currency?: string }) {
  const { data, isLoading, isError, refetch } = useInvoiceItems(id);
  const rows = data?.results ?? [];

  const columns = useMemo<ColumnDef<InvoiceItemRow, unknown>[]>(
    () => [
      {
        id: 'invoice_item_name',
        header: 'Item',
        cell: ({ row }) => {
          const it = row.original;
          const start = it.invoice_item_apply_startdate;
          const end = it.invoice_item_apply_enddate;
          return (
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
              <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflowWrap: 'anywhere' }}>
                {it.invoice_item_name || it.service_name || '—'}
              </span>
              {it.invoice_item_description ? (
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', overflowWrap: 'anywhere' }}>
                  {it.invoice_item_description}
                </span>
              ) : null}
              {start || end ? (
                <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)', whiteSpace: 'nowrap' }}>
                  {formatDate(start)} – {formatDate(end)}
                </span>
              ) : null}
              {it.invoice_item_tags ? (
                <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
                  {it.invoice_item_tags}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'service',
        header: 'Service',
        cell: ({ row }) =>
          row.original.service_id ? (
            <EntityLink
              to={`/services/${row.original.service_id}`}
              id={row.original.service_id}
              name={row.original.service_name || ''}
            />
          ) : (
            <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
          ),
      },
      {
        id: 'quantity',
        header: () => <div style={right}>Qty</div>,
        cell: ({ row }) => {
          const q = num(row.original, 'invoice_item_quantity', 'usage_quantity');
          return <div style={right}>{q != null ? q.toLocaleString('en-US') : '—'}</div>;
        },
      },
      {
        id: 'unit_price',
        header: () => <div style={right}>Unit Price</div>,
        cell: ({ row }) => (
          <div style={right}>{formatMoney(num(row.original, 'invoice_item_unit_price', 'usage_price'), currency)}</div>
        ),
      },
      {
        id: 'discount',
        header: () => <div style={right}>Discount</div>,
        cell: ({ row }) => {
          const d = row.original.invoice_item_discount;
          return <div style={right}>{d ? formatMoney(d, currency) : '—'}</div>;
        },
      },
      {
        id: 'exchange_rate',
        header: () => <div style={right}>Ex. Rate</div>,
        cell: ({ row }) => {
          const r = row.original.exchange_rate;
          return <div style={right}>{r != null && r !== 1 ? r.toLocaleString('en-US') : '—'}</div>;
        },
      },
      {
        id: 'tax',
        header: () => <div style={right}>Tax</div>,
        cell: ({ row }) => {
          const raw = num(row.original, 'invoice_item_tax', 'tax_percentage');
          if (raw == null) return <div style={right}>—</div>;
          // Legacy stores tax as a 0..1 fraction; render as a percentage.
          const pct = raw <= 1 ? raw * 100 : raw;
          return <div style={right}>{`${pct.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`}</div>;
        },
      },
      {
        id: 'amount',
        header: () => <div style={right}>Amount</div>,
        cell: ({ row }) => <div style={right}>{formatMoney(num(row.original, 'invoice_item_amount', 'invoice_item_amount'), currency)}</div>,
      },
    ],
    [currency],
  );

  if (isError) return <TabError title="Failed to load items" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r, i) => String(r.invoice_item_id ?? i)}
      emptyState={<EmptyState title="No items" description="This invoice has no line items." />}
    />
  );
}
